import React, { useMemo, useState } from 'react';
import { Task, User } from '../../types';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addDays,
  differenceInDays,
  isToday,
  isSameDay,
  parseISO,
  addWeeks,
  startOfWeek,
  endOfWeek,
} from 'date-fns';
import {
  Calendar,
  Clock,
  User as UserIcon,
  Flag,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

interface GanttChartProps {
  tasks: Task[];
  users: User[];
  onSelectTask?: (task: Task) => void;
  onTaskUpdate?: (taskId: number, updates: Partial<Task>) => Promise<void>;
}

type ViewMode = 'days' | 'weeks' | 'months';

interface GanttTask extends Task {
  startDate: Date;
  endDate: Date;
  duration: number;
  progress: number;
}

const GanttChart: React.FC<GanttChartProps> = ({
  tasks,
  users,
  onSelectTask,
  onTaskUpdate,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('days');
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Transform tasks to Gantt format
  const ganttTasks = useMemo(() => {
    return tasks
      .filter(task => task.dueDate) // Only tasks with due dates
      .map((task): GanttTask => {
        const startDate = task.createdAt ? parseISO(task.createdAt) : new Date();
        const endDate = task.dueDate ? parseISO(task.dueDate) : addDays(startDate, 7);
        const duration = Math.max(1, differenceInDays(endDate, startDate));

        // Calculate progress based on status
        let progress = 0;
        switch (task.status) {
          case 'completed':
            progress = 100;
            break;
          case 'review':
            progress = 80;
            break;
          case 'in_progress':
            progress = 50;
            break;
          case 'todo':
            progress = 0;
            break;
        }

        return {
          ...task,
          startDate,
          endDate,
          duration,
          progress,
        };
      })
      .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  }, [tasks]);

  // Calculate timeline dates based on view mode
  const timelineData = useMemo(() => {
    let startDate: Date;
    let endDate: Date;

    if (ganttTasks.length === 0) {
      startDate = startOfMonth(selectedDate);
      endDate = endOfMonth(selectedDate);
    } else {
      const earliestStart = ganttTasks.reduce((earliest, task) =>
        task.startDate < earliest ? task.startDate : earliest, ganttTasks[0].startDate);
      const latestEnd = ganttTasks.reduce((latest, task) =>
        task.endDate > latest ? task.endDate : latest, ganttTasks[0].endDate);

      startDate = startOfMonth(earliestStart);
      endDate = endOfMonth(addWeeks(latestEnd, 2));
    }

    const dates = eachDayOfInterval({ start: startDate, end: endDate });

    return { dates, startDate, endDate };
  }, [ganttTasks, selectedDate]);

  // Get column width based on view mode
  const getColumnWidth = () => {
    switch (viewMode) {
      case 'days': return 40;
      case 'weeks': return 80;
      case 'months': return 120;
      default: return 40;
    }
  };

  // Get visible dates based on view mode
  const getVisibleDates = () => {
    const { dates } = timelineData;
    switch (viewMode) {
      case 'days':
        return dates;
      case 'weeks':
        const weeks: Date[] = [];
        let currentWeekStart = startOfWeek(dates[0]);
        while (currentWeekStart <= dates[dates.length - 1]) {
          weeks.push(currentWeekStart);
          currentWeekStart = addWeeks(currentWeekStart, 1);
        }
        return weeks;
      case 'months':
        const months: Date[] = [];
        let currentMonth = startOfMonth(dates[0]);
        while (currentMonth <= dates[dates.length - 1]) {
          months.push(currentMonth);
          currentMonth = addDays(currentMonth, 32); // Move to next month
          currentMonth = startOfMonth(currentMonth);
        }
        return months;
      default:
        return dates;
    }
  };

  const visibleDates = getVisibleDates();
  const columnWidth = getColumnWidth();

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-600';
      case 'review': return 'bg-yellow-600';
      case 'in_progress': return 'bg-blue-600';
      case 'todo': return 'bg-gray-600';
      default: return 'bg-gray-600';
    }
  };

  const calculateTaskPosition = (task: GanttTask) => {
    const { startDate: timelineStart } = timelineData;
    const startOffset = differenceInDays(task.startDate, timelineStart);
    const width = Math.max(task.duration * columnWidth, 20);

    return {
      left: startOffset * columnWidth,
      width: width,
    };
  };

  const formatDateHeader = (date: Date) => {
    switch (viewMode) {
      case 'days':
        return format(date, 'dd');
      case 'weeks':
        return format(date, 'MMM dd');
      case 'months':
        return format(date, 'MMM yyyy');
      default:
        return format(date, 'dd');
    }
  };

  const formatDateSubheader = (date: Date) => {
    switch (viewMode) {
      case 'days':
        return format(date, 'EEE');
      case 'weeks':
        return `Week ${format(date, 'w')}`;
      case 'months':
        return format(date, 'yyyy');
      default:
        return '';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header Controls */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-medium text-gray-900">Timeline View</h3>
            <span className="text-sm text-gray-500">
              {ganttTasks.length} tasks with dates
            </span>
          </div>

          <div className="flex items-center space-x-2">
            {/* View Mode Selector */}
            <div className="flex bg-white border border-gray-300 rounded-lg overflow-hidden">
              {(['days', 'weeks', 'months'] as ViewMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-1 text-sm font-medium capitalize transition-colors ${
                    viewMode === mode
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {mode}
                </button>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setSelectedDate(addDays(selectedDate, -30))}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setSelectedDate(new Date())}
                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
              >
                Today
              </button>
              <button
                onClick={() => setSelectedDate(addDays(selectedDate, 30))}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Gantt Chart */}
      <div className="overflow-auto" style={{ maxHeight: '600px' }}>
        <div className="flex">
          {/* Task Names Column */}
          <div className="w-80 bg-gray-50 border-r border-gray-200 sticky left-0 z-10">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 font-medium text-gray-900 bg-gray-100">
              Task Details
            </div>

            {/* Task List */}
            <div className="divide-y divide-gray-200">
              {ganttTasks.map((task) => {
                const assignedUser = users.find(u => u.id === task.assignedTo);

                return (
                  <div
                    key={task.id}
                    className="p-4 hover:bg-gray-100 cursor-pointer"
                    onClick={() => onSelectTask?.(task)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {task.title || task.name}
                        </h4>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(task.priority)} text-white`}>
                            {task.priority}
                          </span>
                          {assignedUser && (
                            <div className="flex items-center text-xs text-gray-500">
                              <UserIcon className="w-3 h-3 mr-1" />
                              {assignedUser.first_name}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          {format(task.startDate, 'MMM dd')} - {format(task.endDate, 'MMM dd')}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Empty State */}
            {ganttTasks.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <p className="text-sm">No tasks with due dates</p>
                <p className="text-xs text-gray-400">Add due dates to tasks to see them in the timeline</p>
              </div>
            )}
          </div>

          {/* Timeline Column */}
          <div className="flex-1 min-w-0">
            {/* Date Headers */}
            <div className="sticky top-0 z-10 bg-gray-100 border-b border-gray-200">
              <div className="flex" style={{ minWidth: `${visibleDates.length * columnWidth}px` }}>
                {visibleDates.map((date, index) => (
                  <div
                    key={index}
                    className="border-r border-gray-200 text-center py-2 flex-shrink-0"
                    style={{ width: columnWidth }}
                  >
                    <div className="text-xs font-medium text-gray-900">
                      {formatDateHeader(date)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatDateSubheader(date)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline Bars */}
            <div className="relative" style={{ minWidth: `${visibleDates.length * columnWidth}px` }}>
              {/* Background Grid */}
              <div className="absolute inset-0">
                {visibleDates.map((date, index) => (
                  <div
                    key={index}
                    className={`absolute top-0 bottom-0 border-r border-gray-100 ${
                      isToday(date) ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                    style={{
                      left: index * columnWidth,
                      width: columnWidth,
                    }}
                  />
                ))}
              </div>

              {/* Task Bars */}
              <div className="relative">
                {ganttTasks.map((task, index) => {
                  const position = calculateTaskPosition(task);

                  return (
                    <div
                      key={task.id}
                      className="absolute flex items-center"
                      style={{
                        top: index * 73 + 20, // Offset for each task
                        height: 32,
                        left: position.left,
                        width: position.width,
                      }}
                    >
                      {/* Task Bar */}
                      <div
                        className={`h-6 rounded-full shadow-sm cursor-pointer hover:shadow-md transition-all relative overflow-hidden ${getStatusColor(task.status)}`}
                        style={{ width: '100%' }}
                        onClick={() => onSelectTask?.(task)}
                      >
                        {/* Progress Bar */}
                        <div
                          className="absolute top-0 left-0 h-full bg-white bg-opacity-30 rounded-full transition-all"
                          style={{ width: `${task.progress}%` }}
                        />

                        {/* Task Name */}
                        <div className="absolute inset-0 flex items-center px-2">
                          <span className="text-xs text-white font-medium truncate">
                            {task.title || task.name}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Padding for last task */}
              <div style={{ height: ganttTasks.length * 73 + 40 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GanttChart;